import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageProxy from '@next-image-proxy';

import { ExternalLink } from '@uikit/components/ExternalLink';

import { List } from '../../ui/List';
import type { ProjectContentTableOfContentTab } from '../types';
import { about, gallery } from './constants';

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'intro',
    title: 'ChirpPad Introduction',
    content: (
      <>
        <p className="max-sm:text-sm font-normal">
          ChirpPad is a cutting-edge launchpad designed to power the next generation of projects in
          the Web3 ecosystem. Built to provide fair and community-driven access to token launches,
          ChirpPad ensures that dedicated users are rewarded for their engagement!
        </p>
        <p className="max-sm:text-sm font-normal">
          This Launchdrop is a special reward for Magic Store community members who stake $SQR and
          earn SQRp Points.
        </p>
        <div className="overflow-hidden mt-6">
          <Swiper
            modules={[Pagination]}
            pagination={{
              enabled: true,
              clickable: true,
            }}
            slidesPerView="auto"
            className="pb-8"
          >
            {gallery.map((galleryItem, index) => (
              <SwiperSlide key={index} className="w-4/5 px-1">
                <ImageProxy
                  alt={`chirpPad-${index}`}
                  width={640}
                  height={325}
                  quality={100}
                  className="w-full h-auto select-none"
                  src={galleryItem.image}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </>
    ),
  },
  {
    id: 'about',
    title: 'About $CHPD Token',
    content: (
      <>
        <p className="mb-4 max-sm:text-sm font-normal">Get to know the token powering ChirpPad!</p>
        <List items={about} />
      </>
    ),
  },
  {
    id: 'what-is',
    title: 'What is a Launchdrop? (Introducing MagicDrop!)',
    content: (
      <>
        <p className="max-sm:text-sm font-normal">
          A Launchdrop is an exclusive token distribution event where early community supporters
          receive free tokens for their participation and engagement—no payment required!
        </p>
        <p className="max-sm:text-sm font-normal">
          On Magic Square, these events are known as MagicDrops, a reward system designed to give
          back to active Magic Square users and SQR stakers. Instead of paying USDT for tokens,
          users claim them using SQRp Points!
        </p>
      </>
    ),
  },
  {
    id: 'how-work',
    title: 'How MagicDrops Work',
    content: (
      <>
        <p className="max-sm:text-sm font-normal">
          ✅ Earn SQRp Points – Users who{' '}
          <ExternalLink
            className="underline text-base-text-blue text-nowrap"
            href="https://magicsquare.io/staking"
          >
            stake SQR
          </ExternalLink>{' '}
          earn SQRp Points. The more SQR they stake—and the longer they stake it—the more SQRp they
          accumulate. Additionally, being active on the Magic Store earns users daily SQRp,
          rewarding engagement. Learn more about Karma Points and how they boost SQRp earnings{' '}
          <ExternalLink
            className="underline text-base-text-blue text-nowrap"
            href="https://docs.magicsquare.io/magic-store/for-users/magic-karma"
          >
            here
          </ExternalLink>
          .
        </p>
        <p className="max-sm:text-sm font-normal">✅ SQRp Points in Token Sales vs. MagicDrops</p>
        <p className="max-sm:text-sm font-normal">Token Sales: 1 SQRp Point = $1 worth of tokens</p>
        <p className="max-sm:text-sm font-normal">
          MagicDrops: 10 SQRp Points = $1 worth of tokens
        </p>

        <p className="max-sm:text-sm font-normal">
          ✅ Where to Participate – MagicDrops take place on the Magic Launchpad. All upcoming drops
          will be listed on the{' '}
          <ExternalLink
            className="underline text-base-text-blue text-nowrap"
            href="https://magicsquare.io/launchpad/campaigns"
          >
            campaigns page
          </ExternalLink>
          , so users can easily track and participate in the latest opportunities.
        </p>
        <p className="max-sm:text-sm font-normal">
          ✅ Membership-Based Limits – The number of tokens a user can claim depends on their{' '}
          <ExternalLink
            className="underline text-base-text-blue text-nowrap"
            href="https://docs.magicsquare.io/magic-store/for-users/magic-memberships"
          >
            Magic Store membership tier
          </ExternalLink>
          . Higher-tier members can claim more tokens in each drop. These limits may vary per
          campaign based on the reward pool size.
        </p>

        <p className="max-sm:text-sm font-bold">
          📢 MagicDrops are the easiest way for SQR stakers to claim free tokens from projects!
        </p>
      </>
    ),
  },
  {
    id: 'details',
    title: 'ChirpPad MagicDrop Details',
    content: (
      <>
        <p className="max-sm:text-sm font-normal">
          $10,000 in $CHPD available for Magic Square Community
        </p>
        <p className="max-sm:text-sm font-normal">🔹 Max claim: Up to $50 per community member</p>
        <p className="max-sm:text-sm font-normal">
          🔹 First come, first served – Available only between February 21 - February 24
        </p>
        <p className="max-sm:text-sm font-normal">
          🔹 Rewards will be claimable on February 26 directly through{' '}
          <ExternalLink
            className="underline text-base-text-blue text-nowrap"
            href="https://chirppad.ai/"
          >
            ChirpPad’s Claim Portal
          </ExternalLink>
        </p>
        <p className="max-sm:text-sm font-normal">
          🔹 Vesting: Linear release over 5 months starting at TGE
        </p>
        <p className="max-sm:text-sm font-normal">
          🔹 Breakdown of $CHPD Token Allocation & SQRp Points Required
        </p>
        <p className="max-sm:text-sm font-normal mb-6">
          Since 1 $CHPD = $0.006 and 10 SQRp Points = $1 worth of tokens, we calculate:
        </p>
        <ImageProxy
          alt="ChirpPad MagicDrop Details"
          src="/launchpad/stories/chirpPad/details.png"
          className="block w-full h-auto"
          quality={100}
          width={773}
          height={243}
        />
        <p className="max-sm:text-sm font-normal mt-6">
          👉 Want to increase your claim limit?{' '}
          <ExternalLink
            className="underline text-base-text-blue text-nowrap"
            href="https://magicsquare.io/store/profile/membership/dashboard"
          >
            Upgrade your membership
          </ExternalLink>{' '}
          to unlock more rewards!
        </p>
        <p className="max-sm:text-sm font-semibold">
          Learn how to participate in a MagicDrop →{' '}
          <ExternalLink
            className="underline text-base-text-blue text-nowrap font-normal"
            href="https://docs.magicsquare.io/magic-launchpad/for-users/magic-drops"
          >
            Read the guide
          </ExternalLink>
          .
        </p>
      </>
    ),
  },
  {
    id: 'steps-to-claim',
    title: 'Steps to Claim Rewards',
    content: (
      <>
        <p className="max-sm:text-sm font-normal">
          ✅ Step 1: Stake $SQR and earn SQRp Points →{' '}
          <ExternalLink
            className="underline text-base-text-blue text-nowrap"
            href="https://magicsquare.io/staking"
          >
            Start staking
          </ExternalLink>
          .
        </p>
        <p className="max-sm:text-sm font-normal">
          ✅ Step 2 (Optional): Upgrade your Magic Store membership to increase your claim limit →{' '}
          <ExternalLink
            className="underline text-base-text-blue text-nowrap"
            href="https://magicsquare.io/store/profile/membership/dashboard"
          >
            Upgrade here
          </ExternalLink>
          .
        </p>
        <p className="max-sm:text-sm font-normal">
          ✅ Step 3: Use SQRp Points to claim free $CHPD tokens – no payment required!
        </p>
        <p className="max-sm:text-sm font-normal">
          ✅ Step 4: Claim your rewards on February 26th directly from{' '}
          <ExternalLink
            className="underline text-base-text-blue text-nowrap"
            href="https://chirppad.ai/"
          >
            ChirpPad
          </ExternalLink>
          !
        </p>
        <p className="max-sm:text-sm font-normal">
          ✅ Step 5: Vesting: Rewards will unlock gradually over 5 months starting from TGE.
        </p>
        <p className="max-sm:text-sm font-normal">
          ✅ Step 6: Enjoy your $CHPD tokens and be part of the ChirpPad ecosystem!
        </p>
        <p className="max-sm:text-sm font-bold">
          🔥 Don’t miss out – first come, first served! Claim your free $CHPD before the pool runs
          dry!
        </p>
      </>
    ),
  },
];
